import {ProFieldFCRenderProps} from '@ant-design/pro-provider';
import {Cascader, Select, message} from 'antd';
import type {CascaderProps, GetProp} from 'antd';
import React, {useEffect, useMemo, useState} from 'react';
import {requestUrl} from '@oakjs/antd-pro/dist/cjs/services/base/ServiceRequest';
import MaterialKinds from './MaterialKinds';

interface TreeNode {
  id?: number | string;
  code?: string;
  name?: string;
  children?: TreeNode[];
  [key: string]: unknown;
}

interface SelectOption {
  value: string | number;
  label: string;
  children?: SelectOption[];
}

type CascaderOption = GetProp<CascaderProps<SelectOption>, 'options'>[number];

/**
 * 将逗号分隔的表单值转换为编码数组。
 *
 * @param value 当前字段值
 * @returns 编码数组
 */
const splitValue = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value.split(',').map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

/**
 * 将树节点转换为 Cascader 选项。
 *
 * @param nodes 后端树节点
 * @param idKey 作为提交值的字段名
 * @returns Cascader 选项树
 */
const toCascaderOptions = (nodes: TreeNode[], idKey: string): SelectOption[] => (
  nodes.map((node) => ({
    value: String(node[idKey] ?? node.code ?? node.id ?? ''),
    label: String(node.name ?? node.code ?? ''),
    children: node.children?.length ? toCascaderOptions(node.children, idKey) : undefined,
  }))
);

/**
 * 在树中查找指定值的完整路径。
 *
 * @param nodes 后端树节点
 * @param targetValue 目标值
 * @param idKey 作为提交值的字段名
 * @param path 已遍历路径
 * @returns 匹配路径
 */
const findPath = (
  nodes: TreeNode[],
  targetValue: string,
  idKey: string,
  path: string[] = [],
): string[] | undefined => {
  for (const node of nodes) {
    const nodeValue = String(node[idKey] ?? node.code ?? node.id ?? '');
    const nextPath = [...path, nodeValue];
    if (nodeValue === targetValue) {
      return nextPath;
    }
    if (node.children?.length) {
      const childPath = findPath(node.children, targetValue, idKey, nextPath);
      if (childPath) {
        return childPath;
      }
    }
  }
  return undefined;
};

/**
 * 拼接接口地址，避免空参数生成多余问号。
 *
 * @param path 接口路径
 * @param params 查询参数
 * @returns 完整接口地址
 */
const buildUrl = (path: string, params?: string) => (
  params && params.trim() ? `${path}?${params}` : path
);

/**
 * 清理低代码参数中的空值占位。
 *
 * @param params 原始查询参数
 * @returns 过滤后的查询参数
 */
const cleanParams = (params?: string): string => {
  if (!params?.trim()) {
    return '';
  }
  const urlParams = new URLSearchParams(params);
  [...urlParams.entries()].forEach(([key, value]) => {
    if (!value || value === 'undefined' || value === 'null') {
      urlParams.delete(key);
    }
  });
  return urlParams.toString();
};

/**
 * 级联选择品类。
 *
 * @param props 低代码自定义字段参数
 * @returns 品类级联选择器
 */
const MaterialCategory: React.FC<ProFieldFCRenderProps & Record<string, any>> = (props) => {
  const {onChange} = props;
  const idKey = props.idKey || 'code';
  const [sourceTree, setSourceTree] = useState<TreeNode[]>([]);
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchOptions = async () => {
      setLoading(true);
      try {
        const params = cleanParams(props.params) || 'parentCode=ROOT_NODE';
        const response = await requestUrl(buildUrl('/materialcategory/tree', params));
        if (!Array.isArray(response)) {
          message.error('品类数据格式错误');
          return;
        }
        setSourceTree(response);
        setOptions(toCascaderOptions(response, idKey));
      } catch (error) {
        message.error('加载品类失败');
        console.error('Failed to fetch material category options:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOptions();
  }, [idKey, props.params]);

  const selectedValue = useMemo(() => (
    splitValue(props.value).map((value) => findPath(sourceTree, value, idKey)).filter(Boolean) as string[][]
  ), [idKey, props.value, sourceTree]);

  const handleChange: CascaderProps<SelectOption, 'value', true>['onChange'] = (value) => {
    const lastValues = value.map((item) => item[item.length - 1]);
    onChange?.(lastValues.join(','));
  };

  return (
    <Cascader
      allowClear
      changeOnSelect
      loading={loading}
      multiple
      onChange={handleChange}
      options={options}
      showCheckedStrategy={Cascader.SHOW_PARENT}
      showSearch={{
        filter: (inputValue: string, path: CascaderOption[]) => (
          path.some((option) => String(option.label).toLowerCase().includes(inputValue.toLowerCase()))
        ),
      }}
      style={{width: '100%'}}
      value={selectedValue}
    />
  );
};

/**
 * 多选素材标签。
 *
 * @param props 低代码自定义字段参数
 * @returns 标签多选框
 */
const MaterialLabel: React.FC<ProFieldFCRenderProps & Record<string, any>> = (props) => {
  const {onChange} = props;
  const idKey = props.idKey || 'code';
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchOptions = async () => {
      setLoading(true);
      try {
        const response = await requestUrl(buildUrl('/materiallabel/select', cleanParams(props.params)));
        if (!Array.isArray(response)) {
          message.error('标签数据格式错误');
          return;
        }
        setOptions(response.map((item: TreeNode) => ({
          value: String(item[idKey] ?? item.code ?? item.id ?? ''),
          label: String(item.name ?? item.code ?? ''),
        })));
      } catch (error) {
        message.error('加载标签失败');
        console.error('Failed to fetch material label options:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOptions();
  }, [idKey, props.params]);

  const handleChange = (value: Array<string | number>) => {
    onChange?.(value.join(','));
  };

  return (
    <Select
      allowClear
      loading={loading}
      mode="multiple"
      onChange={handleChange}
      optionFilterProp="label"
      options={options}
      showSearch
      style={{width: '100%'}}
      value={splitValue(props.value)}
    />
  );
};

/**
 * 商户端低代码自定义字段入口。
 *
 * @param props ProField 渲染参数
 * @returns 匹配 customType 的字段组件
 */
const CustomComponent: React.FC<ProFieldFCRenderProps & Record<string, any>> = (props) => {
  const customType = props.fieldProps?.customType || props.customType;
  if (props.editable === false) {
    return <span>{props.text || props.value || '-'}</span>;
  }

  switch (customType) {
    case 'CASCADE_MATERIAL_CATEGORY':
      return <MaterialCategory {...props} />;
    case 'CASCADE_MATERIAL_KINDS':
      return <MaterialKinds {...props} />;
    case 'CASCADE_MATERIAL_LABEL':
      return <MaterialLabel {...props} />;
    default:
      return null;
  }
};

export default CustomComponent;
